import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  ArrowLeft,
  Building2,
  MapPin,
  Users,
  Package,
  Award,
  Globe,
  Phone,
  Mail,
  Calendar,
  ExternalLink,
  Briefcase,
  DollarSign,
  Clock,
  Star,
  MessageSquare,
  ShieldCheck
} from 'lucide-react';
import api from '../lib/api';
import { TrustScoreBadge, TrustScoreCard } from '../components/TrustScoreBadge';
import { 
  ReviewsList, 
  TrustBadgesList, 
  RatingStats, 
  WriteReviewDialog 
} from '../components/Reviews';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../i18n';

function getInitials(name) {
  if (!name) return '?';
  return name
    .split(' ')
    .map(part => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export default function DirectoryProfilePage() {
  const { slug: profileId } = useParams();
  const { user } = useAuth();
  const [writeReviewOpen, setWriteReviewOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const { data: profile, isLoading: profileLoading, error } = useQuery({
    queryKey: ['public-profile', profileId],
    queryFn: () => api.get(`/profiles/public/${profileId}`)
  });

  const { data: productsData, isLoading: productsLoading } = useQuery({
    queryKey: ['profile-products', profileId],
    queryFn: () => api.get(`/profiles/directory/${profileId}/products`),
    enabled: !!profile
  });

  // Fetch reviews for this company's user
  const { data: reviewsData } = useQuery({
    queryKey: ['profile-reviews', profile?.user_id],
    queryFn: () => api.get(`/reviews/user/${profile.user_id}`),
    enabled: !!profile?.user_id
  });

  if (profileLoading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="space-y-6 animate-fade-in">
        <Link to="/directory">
          <Button variant="ghost">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Directory
          </Button>
        </Link>
        <Card>
          <CardContent className="py-12 text-center">
            <Building2 className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-medium mb-2">Profile Not Found</h3>
            <p className="text-muted-foreground">
              This company profile may have been removed or set to private.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const products = productsData?.products || [];
  const reviewStats = reviewsData?.stats;
  const reviewsCount = reviewStats?.total_reviews || 0;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="directory-profile-page">
      {/* Back Button */}
      <Link to="/directory">
        <Button variant="ghost" data-testid="back-to-directory">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Directory
        </Button>
      </Link>

      {/* Company Header */}
      <Card data-testid="company-header-card" className="overflow-hidden">
        {/* Banner Image */}
        {profile.banner_url && (
          <div className="h-48 bg-muted">
            <img 
              src={profile.banner_url} 
              alt={`${profile.company_name} banner`}
              className="w-full h-full object-cover"
            />
          </div>
        )}
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-6">
            <div className="flex-1">
              <h1 className="text-2xl font-display font-bold mb-1" data-testid="company-name">
                {profile.company_name}
              </h1>
              {profile.user_id && <TrustScoreBadge userId={profile.user_id} />}
              {profile.verification_tier >= 2 && (
                <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200 gap-1 mb-2">
                  <ShieldCheck className="h-3.5 w-3.5" /> Premium Verified
                </Badge>
              )}
              {profile.verification_tier === 1 && (
                <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 gap-1 mb-2">
                  <ShieldCheck className="h-3.5 w-3.5" /> Verified Business
                </Badge>
              )}
              {profile.tagline && (
                <p className="text-lg text-muted-foreground mb-3">{profile.tagline}</p>
              )}
              <div className="flex flex-wrap gap-2 mb-4">
                {profile.industry && (
                  <Badge variant="secondary">
                    {profile.industry.replace('_', ' ')}
                  </Badge>
                )}
                {profile.company_type && (
                  <Badge variant="outline">
                    {profile.company_type.replace('_', ' ')}
                  </Badge>
                )}
              </div>
              <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                {profile.country && (
                  <div className="flex items-center gap-1">
                    <MapPin className="h-4 w-4" />
                    {profile.city ? `${profile.city}, ${profile.country}` : profile.country}
                  </div>
                )}
                {profile.employee_count && (
                  <div className="flex items-center gap-1">
                    <Users className="h-4 w-4" />
                    {profile.employee_count} employees
                  </div>
                )}
                {profile.year_established && (
                  <div className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    Est. {profile.year_established}
                  </div>
                )}
                {profile.products_count > 0 && (
                  <div className="flex items-center gap-1">
                    <Package className="h-4 w-4" />
                    {profile.products_count} products/services
                  </div>
                )}
                {reviewStats && reviewStats.total_reviews > 0 && (
                  <div className="flex items-center gap-1">
                    <Star className="h-4 w-4 text-amber-400 fill-amber-400" />
                    {reviewStats.average_rating?.toFixed(1)} ({reviewStats.total_reviews} reviews)
                  </div>
                )}
              </div>
              {/* Trust Badges */}
              {profile?.user_id && (
                <div className="mt-4">
                  <TrustBadgesList userId={profile.user_id} />
                </div>
              )}
            </div>

          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="about" className="space-y-4">
        <TabsList>
          <TabsTrigger value="about" data-testid="tab-about">
            <Building2 className="h-4 w-4 mr-2" />
            About
          </TabsTrigger>
          <TabsTrigger value="products" data-testid="tab-products">
            <Package className="h-4 w-4 mr-2" />
            Products ({products.length})
          </TabsTrigger>
          <TabsTrigger value="contact" data-testid="tab-contact">
            <Phone className="h-4 w-4 mr-2" />
            Contact
          </TabsTrigger>
          <TabsTrigger value="reviews" data-testid="tab-reviews">
            <Star className="h-4 w-4 mr-2" />
            Reviews ({reviewsCount})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="about">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Description */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>About</CardTitle>
              </CardHeader>
              <CardContent>
                {profile.description ? (
                  <p className="text-muted-foreground whitespace-pre-wrap">
                    {profile.description}
                  </p>
                ) : (
                  <p className="text-muted-foreground italic">No description provided.</p>
                )}
              </CardContent>
            </Card>

            {/* Quick Info */}
            <Card>
              <CardHeader>
                <CardTitle>Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {profile.certifications?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium mb-2 flex items-center gap-2">
                      <Award className="h-4 w-4 text-amber-500" />
                      Certifications
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {profile.certifications.map(cert => (
                        <Badge key={cert} variant="outline" className="text-xs">
                          {cert}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                {profile.languages_spoken?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium mb-2 flex items-center gap-2">
                      <Globe className="h-4 w-4 text-blue-500" />
                      Languages
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {profile.languages_spoken.join(', ')}
                    </p>
                  </div>
                )}
                {profile.shipping_regions?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium mb-2 flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-green-500" />
                      Shipping Regions
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {profile.shipping_regions.join(', ')}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="products">
          {productsLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3].map(i => (
                <Skeleton key={i} className="h-48" />
              ))}
            </div>
          ) : products.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Package className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                <h3 className="text-lg font-medium mb-2">No products listed</h3>
                <p className="text-muted-foreground">
                  This company hasn't added any products or services yet.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {products.map(product => (
                <Card key={product._id} className="overflow-hidden cursor-pointer hover:shadow-md transition-shadow" data-testid={`product-card-${product._id}`} onClick={() => setSelectedProduct(product)}>
                  {/* Product Image */}
                  {product.images?.[0] && (
                    <div className="aspect-video bg-muted">
                      <img 
                        src={product.images[0]} 
                        alt={product.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-2 mb-1">
                      {product.is_service ? (
                        <Briefcase className="h-4 w-4 text-blue-500" />
                      ) : (
                        <Package className="h-4 w-4 text-green-500" />
                      )}
                      <Badge variant="secondary" className="text-xs">
                        {product.category || 'Uncategorized'}
                      </Badge>
                    </div>
                    <CardTitle className="text-lg">{product.name}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                      {product.description || 'No description'}
                    </p>
                    <div className="flex items-center justify-between text-sm">
                      {product.price ? (
                        <div className="flex items-center gap-1">
                          <DollarSign className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">
                            {product.currency} {product.price.toLocaleString()}
                          </span>
                          <span className="text-muted-foreground text-xs">
                            /{product.price_unit?.replace('per_', '')}
                          </span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">Price on request</span>
                      )}
                      {product.lead_time_days && (
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Clock className="h-4 w-4" />
                          {product.lead_time_days}d
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="contact">
          <Card>
            <CardHeader>
              <CardTitle>Contact Information</CardTitle>
              <CardDescription>
                Get in touch with {profile.company_name}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {profile.website && (
                <div className="flex items-center gap-3">
                  <Globe className="h-5 w-5 text-muted-foreground" />
                  <a 
                    href={profile.website} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary hover:underline flex items-center gap-1"
                  >
                    {profile.website}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              )}
              {profile.email && (
                <div className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-muted-foreground" />
                  <a href={`mailto:${profile.email}`} className="text-primary hover:underline">
                    {profile.email}
                  </a>
                </div>
              )}
              {profile.phone && (
                <div className="flex items-center gap-3">
                  <Phone className="h-5 w-5 text-muted-foreground" />
                  <a href={`tel:${profile.phone}`} className="text-primary hover:underline">
                    {profile.phone}
                  </a>
                </div>
              )}
              {profile.address && (
                <div className="flex items-center gap-3">
                  <MapPin className="h-5 w-5 text-muted-foreground" />
                  <span>{profile.address}</span>
                </div>
              )}
              {!profile.website && !profile.email && !profile.phone && !profile.address && (
                <p className="text-muted-foreground italic">
                  No contact information provided.
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="reviews">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Rating Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Star className="h-5 w-5 text-amber-400" />
                  Ratings & Reviews
                </CardTitle>
              </CardHeader>
              <CardContent>
                {reviewStats && reviewStats.total_reviews > 0 ? (
                  <RatingStats stats={reviewStats} />
                ) : (
                  <p className="text-muted-foreground text-center py-4">
                    No reviews yet
                  </p>
                )}
                {user && profile?.user_id && user._id !== profile.user_id && (
                  <Button 
                    className="w-full mt-4"
                    onClick={() => setWriteReviewOpen(true)}
                    data-testid="write-review-btn"
                  >
                    <MessageSquare className="h-4 w-4 mr-2" />
                    Write a Review
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Reviews List */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Customer Reviews</CardTitle>
              </CardHeader>
              <CardContent>
                {profile?.user_id ? (
                  <ReviewsList userId={profile.user_id} />
                ) : (
                  <p className="text-muted-foreground text-center py-8">
                    No reviews available
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>


      {/* Product Detail Modal */}
      <Dialog open={!!selectedProduct} onOpenChange={(open) => { if (!open) setSelectedProduct(null); }}>
        <DialogContent className="w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedProduct && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 mb-1">
                  {selectedProduct.is_service ? (
                    <Briefcase className="h-5 w-5 text-blue-500" />
                  ) : (
                    <Package className="h-5 w-5 text-green-500" />
                  )}
                  <Badge variant="secondary">{selectedProduct.category || 'Uncategorized'}</Badge>
                  {selectedProduct.subcategory && (
                    <Badge variant="outline" className="text-xs">{selectedProduct.subcategory}</Badge>
                  )}
                </div>
                <DialogTitle className="text-xl">{selectedProduct.name}</DialogTitle>
              </DialogHeader>

              {/* Large Image */}
              {selectedProduct.images?.[0] && (
                <div className="rounded-lg overflow-hidden bg-muted">
                  <img
                    src={selectedProduct.images[0]}
                    alt={selectedProduct.name}
                    className="w-full max-h-80 object-contain"
                  />
                </div>
              )}

              {/* Price & Availability */}
              <div className="flex flex-wrap items-center gap-4 py-3 border-y">
                {selectedProduct.price ? (
                  <div className="flex items-center gap-1">
                    <DollarSign className="h-5 w-5 text-primary" />
                    <span className="text-2xl font-bold">
                      {selectedProduct.currency} {selectedProduct.price.toLocaleString()}
                    </span>
                    <span className="text-muted-foreground">
                      /{selectedProduct.price_unit?.replace('per_', '')}
                    </span>
                  </div>
                ) : (
                  <span className="text-lg text-muted-foreground">Price on request</span>
                )}
                {selectedProduct.min_order_quantity && (
                  <Badge variant="outline">MOQ: {selectedProduct.min_order_quantity} units</Badge>
                )}
                {selectedProduct.lead_time_days && (
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <Clock className="h-4 w-4" />
                    <span>{selectedProduct.lead_time_days} day lead time</span>
                  </div>
                )}
              </div>

              {/* Full Description */}
              <div>
                <h4 className="font-medium mb-2">Description</h4>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {selectedProduct.description || 'No description available.'}
                </p>
              </div>

              {/* Specifications */}
              {selectedProduct.specifications && Object.keys(selectedProduct.specifications).length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Specifications</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(selectedProduct.specifications).map(([key, value]) => (
                      <div key={key} className="flex justify-between p-2 bg-muted/50 rounded text-sm">
                        <span className="text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</span>
                        <span className="font-medium">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Contact Button */}
              <Button className="w-full mt-2" onClick={() => { setSelectedProduct(null); document.querySelector('[value="contact"]')?.click(); }}>
                <Mail className="h-4 w-4 mr-2" />
                Contact Seller About This {selectedProduct.is_service ? 'Service' : 'Product'}
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Write Review Dialog */}
      {profile?.user_id && (
        <WriteReviewDialog
          targetUserId={profile.user_id}
          targetUserName={profile.company_name}
          open={writeReviewOpen}
          onOpenChange={setWriteReviewOpen}
        />
      )}
    </div>
  );
}
